import {
  CdkDragEnter,
  CdkDragExit,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray
} from "@angular/cdk/drag-drop";
import { ViewportRuler } from "@angular/cdk/scrolling";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { Subject } from "rxjs";
import { Finishing } from 'src/app/shared-lib/core/settings';
import { PrintingGroup, ProductService } from 'src/app/shared-lib/domains/product';
import { AppService } from "../../shared/app.service";
import { NotificationsService } from "../../shared/notifications.service";

@Component({
  selector: "app-files-container",
  templateUrl: "./files-container.component.html",
  styleUrls: ["./files-container.component.scss"],
  standalone: false
})
export class FilesContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() files;
  private _containerWidth: number;

  @Input() set containerWidth(value: number) {
    this._containerWidth = value;
    this.adjustFileColspan();
  }

  @Input() printingGroup: PrintingGroup;
  @Input() printProduct;
  @Input() printProductId: string = null;
  @Input() settings;

  @Input() settingsChanged;
  @Input() isLoading = false;
  @Output() fileAction = new EventEmitter<any>();
  @ViewChild(CdkDropListGroup) listGroup: CdkDropListGroup<CdkDropList>;
  @ViewChild(CdkDropList) placeholder: CdkDropList;

  destroyed = new Subject<void>();

  public fileColspan: number = 6;
  public rowHeight = "570px";
  isA3Binding: boolean = false;
  hideGroupedFiles = false;

  constructor(
    private viewportRuler: ViewportRuler,
    public appService: AppService,
    private notificationsService: NotificationsService,
    private productService: ProductService,
  ) {
  }

  get warningMessage() {
    return this.productService.getWarningMessage(this.printingGroup);
  }

  ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  ngOnInit() {
    this.adjustFileColspan();
    this.isA3Binding = this.printingGroup.printSettings?.pageSize === 'A3' && this.printingGroup.printSettings?.finishing === Finishing.BINDING;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.settingsChanged) {
      this.adjustFileColspan();
      this.isA3Binding = this.printingGroup.printSettings?.pageSize === 'A3' && this.printingGroup.printSettings?.finishing === Finishing.BINDING;
    }
  }

  _fileAction(event, i) {
    const data = { event, i };
    this.fileAction.emit(data);
  }

  ngAfterViewInit() {

  }

  adjustFileColspan() {
    // Paso 1: Si el containerWidth aún no se ha recibido, no hacemos nada.
    // Esto evita que se use 'undefined' o '0' en los cálculos.
    if (!this._containerWidth || this._containerWidth === 0) {
      return;
    }

    const width = this._containerWidth;
    if (width < 1104) {
      this.fileColspan = 12; // 1 columna
    } else if (width < 1580) {
      this.fileColspan = 6;  // 2 columnas
    } else {
      this.fileColspan = 4;  // 3 columnas
    }

    if (this.appService.isMobile()) {
      this.rowHeight = "200px";
    } else {
      this.rowHeight = "570px";
      if (this.printingGroup.printSettings.finishing === Finishing.BINDING_LAMINATED) {
        this.rowHeight = "480px";
      }
    }

    if (this.coverLaminatedEnabled()) {
      const heightValue = this.rowHeight.replace("px", "");
      const totalHeight = (Number(heightValue) + 0).toString(10);
      this.rowHeight = totalHeight + "px";
    }
    //if is binding individual sum 30px
    if (this.printingGroup.printSettings.finishing === Finishing.BINDING && !this.printingGroup.printSettings.grouped) {
      const heightValue = this.rowHeight.replace("px", "");
      const totalHeight = (Number(heightValue) + 15).toString(10);
      this.rowHeight = totalHeight + "px";
    }
  }

  drop(event) {
    const sourceIndex = event.item.data;
    const targetIndex = event.container.data;
    moveItemInArray(this.files, sourceIndex, targetIndex);
    moveItemInArray(this.printingGroup.files, sourceIndex, targetIndex);
    this._fileAction({ action: "reorder" }, 0);

    // Quitar la opacidad de los elementos al soltarlos
    const containerDestination = event.container.element.nativeElement as HTMLElement;
    const cdkDragElements = Array.from(containerDestination.querySelectorAll(".cdk-drag"));
    cdkDragElements.forEach(childDiv => {
      const appFileComponent = childDiv.querySelector("app-file") as HTMLElement;
      appFileComponent.classList.remove("add-opacity");
    });

    //Toggle
    if (sourceIndex !== targetIndex) {
      this.notificationsService.showToast("Tu documento se ha movido con éxito", "success");
    }
  }

  dragEnter(event: CdkDragEnter) {
    const containerDestination = event.container.element.nativeElement;

    const placeholder = containerDestination.querySelector(".cdk-drag-placeholder") as HTMLElement;
    if (placeholder) {
      placeholder.classList.add("hidden-placeholder");
      const sibling = placeholder.nextElementSibling as HTMLElement;
      if (sibling) {
        sibling.classList.add("no-transform");
      }
    }

    const cdkDragElements = Array.from(containerDestination.querySelectorAll(".cdk-drag"));
    cdkDragElements.forEach(childDiv => {
      const appFileComponent = childDiv.querySelector("app-file") as HTMLElement;
      appFileComponent.classList.add("add-opacity");
    });
  }

  dragExit(event: CdkDragExit) {
    const containerDestination = event.container.element.nativeElement;
    if (containerDestination.classList.contains("cdk-drop-list-dragging")) {
      const cdkDragElements = Array.from(containerDestination.querySelectorAll(".cdk-drag"));
      cdkDragElements.forEach(childDiv => {
        const appFileComponent = childDiv.querySelector("app-file") as HTMLElement;
        appFileComponent.classList.remove("add-opacity");
      });
    }
  }

  protected readonly Finishing = Finishing;

  coverLaminatedEnabled() {
    return this.printingGroup.files.some(f => f.coverLaminated);
  }

  getRowspanForGroupedBinding() {
    let rowspan = 11;
    if (this.coverLaminatedEnabled()) {
      rowspan += 2;
    }
    if (this.appService.isMobile()) {
      rowspan -= 7;
    }
    return rowspan;
  }

  toggleHideGroupedFiles(event) {
    this.hideGroupedFiles = event;
  }
}

